import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, MagnifyingGlass, ArrowDown, ArrowUp, Wallet, CurrencyInr,
  TrendUp, Users, Warning, Funnel, Export, Trash, PencilSimple,
  Stethoscope, HandCoins, ClockCounterClockwise, Receipt, CaretRight, Printer
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function KhataPage() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
        Khata Book
      </h1>
      <Tabs defaultValue="dashboard">
        <TabsList className="flex-wrap">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="ledger">Party Ledger</TabsTrigger>
          <TabsTrigger value="doctor-ledger">Doctor Ledger</TabsTrigger>
          <TabsTrigger value="passbook">Passbook</TabsTrigger>
          <TabsTrigger value="audit">Audit Trail</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><KhataDashboard /></TabsContent>
        <TabsContent value="ledger"><KhataLedger /></TabsContent>
        <TabsContent value="doctor-ledger"><DoctorLedgerTab /></TabsContent>
        <TabsContent value="passbook"><PassbookTab /></TabsContent>
        <TabsContent value="audit"><AuditTrail /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════════════════
function KhataDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.getKhataDashboard().then(r => { setData(r.data); setLoading(false); }); }, []);

  if (loading) return <p className="py-8 text-center" style={{ color: '#8A94A6' }}>Loading dashboard...</p>;
  if (!data) return null;

  return (
    <div className="space-y-6 mt-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <DashCard icon={ArrowDown} iconColor="#34C759" iconBg="#34C75910" label="Total Receivable" value={`₹${data.total_receivable.toLocaleString()}`} valueColor="#34C759" />
        <DashCard icon={ArrowUp} iconColor="#FF3B30" iconBg="#FF3B3010" label="Total Payable" value={`₹${data.total_payable.toLocaleString()}`} valueColor="#FF3B30" />
        <DashCard icon={Wallet} iconColor="#007AFF" iconBg="#007AFF10" label="Net Balance"
          value={`₹${Math.abs(data.net_balance).toLocaleString()}`} valueColor={data.net_balance >= 0 ? '#34C759' : '#FF3B30'}
          subtitle={data.net_balance >= 0 ? 'You will receive' : 'You will pay'} />
        <DashCard icon={Warning} iconColor="#FFCC00" iconBg="#FFCC0010" label="Overdue" value={data.overdue_count} valueColor="#FF3B30" subtitle={`of ${data.outstanding_customers} outstanding`} />
      </div>

      {/* Monthly Trend Chart */}
      <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Monthly Receivables vs Payables</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.monthly_trend || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
            <Bar dataKey="receivable" fill="#34C759" radius={[3,3,0,0]} name="Receivable" />
            <Bar dataKey="payable" fill="#FF3B30" radius={[3,3,0,0]} name="Payable" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Transactions */}
      <div className="rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#E2E4E9' }}>
          <h3 className="text-sm font-semibold" style={{ color: '#0A2540' }}>Recent Transactions</h3>
        </div>
        <div className="divide-y" style={{ borderColor: '#E2E4E9' }}>
          {(data.recent_transactions || []).map((t, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: t.entry_type === 'receivable' ? '#34C75910' : '#FF3B3010' }}>
                {t.entry_type === 'receivable' ? <ArrowDown size={16} weight="bold" style={{ color: '#34C759' }} /> : <ArrowUp size={16} weight="bold" style={{ color: '#FF3B30' }} />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: '#0A2540' }}>{t.customer_name}</p>
                <p className="text-[10px]" style={{ color: '#8A94A6' }}>{t.description} | {t.date} | {t.payment_mode || 'cash'}</p>
              </div>
              <p className="text-sm font-bold" style={{ color: t.entry_type === 'receivable' ? '#34C759' : '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>
                {t.entry_type === 'receivable' ? '+' : '-'}₹{t.amount?.toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DashCard({ icon: Icon, iconColor, iconBg, label, value, valueColor, subtitle }) {
  return (
    <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
          <Icon size={16} weight="duotone" style={{ color: iconColor }} />
        </div>
        <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{label}</span>
      </div>
      <p className="text-xl font-extrabold" style={{ color: valueColor || '#0A2540', fontVariantNumeric: 'tabular-nums', fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      {subtitle && <p className="text-[10px] mt-0.5" style={{ color: '#8A94A6' }}>{subtitle}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// LEDGER TAB — Khatabook Desktop Style
// ═══════════════════════════════════════════════════════
function KhataLedger() {
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSummary = () => { api.getKhataSummary().then(r => setSummary(r.data)); };
  const loadEntries = (custId) => {
    const params = {};
    if (custId) params.customer_id = custId;
    const mFrom = dateFrom || `${month}-01`;
    const mTo = dateTo || `${month}-31`;
    params.date_from = mFrom;
    params.date_to = mTo;
    api.getKhata(params).then(r => setEntries(r.data));
  };

  useEffect(() => { loadSummary(); setLoading(false); }, []);
  useEffect(() => { loadEntries(selectedCustomer?.customer_id); }, [selectedCustomer, dateFrom, dateTo, month]);

  const filteredSummary = summary.filter(s => !search || s.customer_name?.toLowerCase().includes(search.toLowerCase()));

  const exportCSV = async () => {
    const params = {};
    if (selectedCustomer) params.customer_id = selectedCustomer.customer_id;
    params.date_from = dateFrom || `${month}-01`;
    params.date_to = dateTo || `${month}-31`;
    const { data } = await api.getKhataExport(params);
    const csv = ['Date,Customer,Type,Description,Payment Mode,Reference,Amount,Balance'];
    data.forEach(r => csv.push(`${r.date},${r.customer},${r.type},${r.description},${r.payment_mode},${r.reference},${r.amount},${r.balance}`));
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `khata_export_${month}.csv`; a.click();
  };

  const exportPDF = () => {
    window.open(api.getKhataExportPdfUrl(selectedCustomer?.customer_id || '', month), '_blank');
  };

  // Running balance for selected customer
  let runBal = 0;
  const entriesWithBalance = [...entries].reverse().map(e => {
    if (e.entry_type === 'receivable') runBal += e.amount;
    else runBal -= e.amount;
    return { ...e, running_balance: runBal };
  }).reverse();

  return (
    <>
    <div className="mt-4 flex gap-4" style={{ minHeight: 500 }}>
      {/* Left: Customer List */}
      <div className="w-72 shrink-0 rounded-md border flex flex-col" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="p-3 border-b" style={{ borderColor: '#E2E4E9' }}>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-8 h-8 text-xs" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <button onClick={() => setSelectedCustomer(null)} className="w-full px-3 py-2.5 text-left hover:bg-slate-50 flex items-center gap-2 transition-colors"
            style={{ background: !selectedCustomer ? '#007AFF08' : 'transparent', borderBottom: '1px solid #E2E4E9' }}>
            <Users size={14} style={{ color: '#007AFF' }} />
            <span className="text-xs font-bold" style={{ color: '#0A2540' }}>All Accounts</span>
          </button>
          {filteredSummary.map((s, i) => (
            <button key={i} onClick={() => setSelectedCustomer(s)}
              className="w-full px-3 py-2.5 text-left hover:bg-slate-50 flex items-center justify-between transition-colors"
              style={{ background: selectedCustomer?.customer_id === s.customer_id ? '#007AFF08' : 'transparent', borderBottom: '1px solid #F5F6F8' }}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                  style={{ background: s.net >= 0 ? '#34C759' : '#FF3B30' }}>{s.customer_name?.charAt(0)}</div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: '#0A2540' }}>{s.customer_name}</p>
                  <p className="text-[9px]" style={{ color: '#8A94A6' }}>{s.txn_count} txns</p>
                </div>
              </div>
              <span className="text-xs font-bold shrink-0" style={{ color: s.net >= 0 ? '#34C759' : '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>
                ₹{Math.abs(s.net).toLocaleString()}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Transaction Details */}
      <div className="flex-1 space-y-3">
        {/* Header + Actions */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
              {selectedCustomer ? selectedCustomer.customer_name : 'All Transactions'}
            </h3>
            {selectedCustomer && (
              <p className="text-xs" style={{ color: '#8A94A6' }}>
                Receivable: <span style={{ color: '#34C759' }}>₹{selectedCustomer.receivable?.toLocaleString()}</span> |
                Payable: <span style={{ color: '#FF3B30' }}>₹{selectedCustomer.payable?.toLocaleString()}</span> |
                Net: <span style={{ color: selectedCustomer.net >= 0 ? '#34C759' : '#FF3B30', fontWeight: 700 }}>₹{Math.abs(selectedCustomer.net)?.toLocaleString()}</span>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={exportCSV} variant="outline" className="h-8 px-3 text-xs font-semibold rounded" style={{ borderColor: '#E2E4E9' }}>
              <Export size={14} className="mr-1" /> CSV
            </Button>
            <Button onClick={exportPDF} variant="outline" className="h-8 px-3 text-xs font-semibold rounded" style={{ borderColor: '#E2E4E9' }}>
              <Printer size={14} className="mr-1" /> PDF
            </Button>
            <Button onClick={() => setShowForm(true)} className="h-8 px-3 text-xs font-semibold rounded" style={{ background: '#0A2540', color: 'white' }}>
              <Plus size={14} className="mr-1" /> New Entry
            </Button>
          </div>
        </div>

        {/* Month Selector + Filters */}
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>Month</Label>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-7 text-xs w-40" />
          </div>
            <Label className="text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs w-36" />
          </div>
          <div>
            <Label className="text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs w-36" />
          </div>
          {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[10px] font-medium" style={{ color: '#007AFF' }}>Clear</button>}
        </div>

        {/* Transaction Table */}
        <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <table className="w-full text-xs" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <thead><tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
              {['Date', 'Customer', 'Description', 'Mode', 'Ref', 'Debit', 'Credit', 'Balance', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {entriesWithBalance.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm" style={{ color: '#8A94A6' }}>No transactions</td></tr>
              ) : entriesWithBalance.map((e, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #F5F6F8' }}>
                  <td className="px-3 py-2" style={{ color: '#8A94A6' }}>{e.date}</td>
                  <td className="px-3 py-2 font-medium" style={{ color: '#0A2540' }}>{e.customer_name}</td>
                  <td className="px-3 py-2" style={{ color: '#8A94A6' }}>{e.description}</td>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: '#F0F4F8' }}>{e.payment_mode || 'cash'}</span></td>
                  <td className="px-3 py-2" style={{ color: '#8A94A6' }}>{e.reference_number || '-'}</td>
                  <td className="px-3 py-2 text-right font-medium" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>
                    {e.entry_type === 'receivable' ? `₹${e.amount.toLocaleString()}` : ''}
                  </td>
                  <td className="px-3 py-2 text-right font-medium" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>
                    {e.entry_type === 'payable' ? `₹${e.amount.toLocaleString()}` : ''}
                  </td>
                  <td className="px-3 py-2 text-right font-bold" style={{ color: e.running_balance >= 0 ? '#FF3B30' : '#34C759', fontVariantNumeric: 'tabular-nums' }}>
                    ₹{Math.abs(e.running_balance).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={async () => { if (window.confirm('Delete?')) { await api.deleteKhataEntry(e._id); loadSummary(); loadEntries(selectedCustomer?.customer_id); }}}
                      className="p-0.5 rounded hover:bg-red-50"><Trash size={12} style={{ color: '#FF3B30' }} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Entry Dialog */}
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>New Khata Entry</DialogTitle></DialogHeader>
          <KhataEntryForm customerId={selectedCustomer?.customer_id} customerName={selectedCustomer?.customer_name} onSave={() => { setShowForm(false); loadSummary(); loadEntries(selectedCustomer?.customer_id); }} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function KhataEntryForm({ customerId, customerName, onSave }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: customerId || '', customer_name: customerName || '',
    entry_type: 'receivable', amount: 0, description: '',
    payment_mode: 'cash', reference_number: '', date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { if (!customerId) api.getCustomers().then(r => setCustomers(r.data)); }, [customerId]);

  const save = async () => {
    try { await api.createKhataEntry(form); onSave(); } catch {}
  };

  return (
    <div className="space-y-3">
      {!customerId && (
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Customer / Doctor</Label>
          <Select value={form.customer_id} onValueChange={v => { const c = customers.find(x => x.id === v); setForm(f => ({ ...f, customer_id: v, customer_name: c?.name || '' })); }}>
            <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Type</Label>
          <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
            <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="receivable">You Will Get (Receivable)</SelectItem>
              <SelectItem value="payable">You Will Pay (Payable)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Date</Label>
          <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-9 mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Amount</Label>
        <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} className="h-9 mt-1 text-lg font-bold" placeholder="0" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Payment Mode</Label>
          <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}>
            <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank">Bank Transfer</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Reference #</Label>
          <Input value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} className="h-9 mt-1" placeholder="Txn/Cheque #" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Description</Label>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-9 mt-1" />
      </div>
      <Button onClick={save} className="w-full h-10 text-sm font-bold"
        style={{ background: form.entry_type === 'receivable' ? '#34C759' : '#FF3B30', color: 'white' }}>
        {form.entry_type === 'receivable' ? '+ You Will Get' : '- You Will Pay'} ₹{form.amount.toLocaleString()}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// DOCTOR LEDGER TAB (Merged Payouts + Commission)
// ═══════════════════════════════════════════════════════
function DoctorLedgerTab() {
  const [payoutData, setPayoutData] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [search, setSearch] = useState('');

  const loadList = () => { api.getDoctorPayouts().then(r => { setPayoutData(r.data); setLoading(false); }); };
  useEffect(() => { loadList(); }, []);

  const loadLedger = (docId) => {
    if (!docId) { setLedger(null); return; }
    const from = dateFrom || `${month}-01`;
    const to = dateTo || `${month}-31`;
    api.getDoctorLedgerFull(docId, from, to).then(r => setLedger(r.data));
  };
  useEffect(() => { if (selectedDoctor) loadLedger(selectedDoctor.doctor_id); }, [selectedDoctor, dateFrom, dateTo, month]);

  const exportCSV = async () => {
    if (!selectedDoctor) return;
    const { data } = await api.exportDoctorLedger(selectedDoctor.doctor_id);
    const csv = ['Date,Type,Description,Invoice,Sale Amount,Commission Earned,Payout,Running Balance'];
    data.forEach(r => csv.push(`${r.date},${r.type},${r.description},${r.invoice_number},${r.sale_amount},${r.commission_earned},${r.payout},${r.running_balance}`));
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `doctor_ledger_${selectedDoctor.doctor_name}_${month}.csv`; a.click();
  };

  const exportPDF = () => {
    if (!selectedDoctor) return;
    window.open(api.getDoctorLedgerPdfUrl(selectedDoctor.doctor_id, month), '_blank');
  };

  if (loading) return <p className="py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</p>;

  const doctors = payoutData?.doctors || [];
  const filtered = doctors.filter(d => !search || d.doctor_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="mt-4 flex gap-4" style={{ minHeight: 520 }}>
      {/* Left: Doctor List */}
      <div className="w-72 shrink-0 rounded-md border flex flex-col" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="p-3 border-b space-y-2" style={{ borderColor: '#E2E4E9' }}>
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded" style={{ background: '#FF3B3008' }}>
              <p className="text-[8px] font-bold uppercase" style={{ color: '#8A94A6' }}>Pending</p>
              <p className="text-sm font-extrabold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{(payoutData?.total_pending || 0).toLocaleString()}</p>
            </div>
            <div className="p-2 rounded" style={{ background: '#34C75908' }}>
              <p className="text-[8px] font-bold uppercase" style={{ color: '#8A94A6' }}>Paid</p>
              <p className="text-sm font-extrabold" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{(payoutData?.total_paid || 0).toLocaleString()}</p>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search doctor..." className="pl-8 h-7 text-xs" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map((d, i) => (
            <button key={i} onClick={() => setSelectedDoctor(d)}
              className="w-full px-3 py-2.5 text-left hover:bg-slate-50 flex items-center justify-between transition-colors"
              style={{ background: selectedDoctor?.doctor_id === d.doctor_id ? '#007AFF08' : 'transparent', borderBottom: '1px solid #F5F6F8' }}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded flex items-center justify-center shrink-0" style={{ background: '#007AFF10' }}>
                  <Stethoscope size={13} style={{ color: '#007AFF' }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: '#0A2540' }}>{d.doctor_name}</p>
                  <p className="text-[9px]" style={{ color: '#8A94A6' }}>{d.commission_percent}% | {d.specialization}</p>
                </div>
              </div>
              <span className="text-[10px] font-bold shrink-0" style={{ color: d.pending > 0 ? '#FF3B30' : '#34C759', fontVariantNumeric: 'tabular-nums' }}>
                ₹{d.pending.toLocaleString()}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Doctor Ledger Detail */}
      <div className="flex-1 space-y-3">
        {!selectedDoctor ? (
          <div className="p-16 text-center rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <Stethoscope size={48} weight="duotone" style={{ color: '#E2E4E9' }} className="mx-auto mb-3" />
            <p className="text-sm font-medium" style={{ color: '#8A94A6' }}>Select a doctor to view their commission ledger</p>
          </div>
        ) : !ledger ? (
          <p className="py-8 text-center" style={{ color: '#8A94A6' }}>Loading ledger...</p>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>{ledger.doctor_name}</h3>
              <div className="flex gap-2">
                <Button onClick={exportCSV} variant="outline" className="h-8 px-3 text-xs font-semibold rounded" style={{ borderColor: '#E2E4E9' }}>
                  <Export size={14} className="mr-1" /> CSV
                </Button>
                <Button onClick={exportPDF} variant="outline" className="h-8 px-3 text-xs font-semibold rounded" style={{ borderColor: '#E2E4E9' }}>
                  <Printer size={14} className="mr-1" /> PDF
                </Button>
                {ledger.pending_commission > 0 && (
                  <Button onClick={() => setShowPayDialog(true)} className="h-8 px-3 text-xs font-bold rounded" style={{ background: '#34C759', color: 'white' }}>
                    <HandCoins size={14} className="mr-1" /> Record Payout
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Commission Earned</p>
                <p className="text-xl font-extrabold mt-1" style={{ color: '#007AFF', fontVariantNumeric: 'tabular-nums' }}>₹{ledger.total_commission_earned.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Payouts Made</p>
                <p className="text-xl font-extrabold mt-1" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{ledger.total_payouts_made.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-md border" style={{ background: ledger.pending_commission > 0 ? '#FFF0F0' : '#F0FFF4', borderColor: ledger.pending_commission > 0 ? '#FF3B3030' : '#34C75930' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Pending Payable</p>
                <p className="text-xl font-extrabold mt-1" style={{ color: ledger.pending_commission > 0 ? '#FF3B30' : '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{ledger.pending_commission.toLocaleString()}</p>
              </div>
            </div>

            {/* Month + Date Filters */}
            <div className="flex gap-2 items-end">
              <div><Label className="text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>Month</Label>
                <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-7 text-xs w-40" /></div>
              <div><Label className="text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>From</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs w-36" /></div>
              <div><Label className="text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>To</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs w-36" /></div>
              {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[10px] font-medium" style={{ color: '#007AFF' }}>Clear</button>}
            </div>

            {/* Ledger Table */}
            <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <table className="w-full text-xs" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <thead><tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
                  {['Date', 'Type', 'Description', 'Invoice', 'Sale Amt', 'Commission', 'Payout', 'Balance'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(ledger.entries || []).length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No transactions found</td></tr>
                  ) : (ledger.entries || []).map((e, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #F5F6F8' }}>
                      <td className="px-3 py-2" style={{ color: '#8A94A6' }}>{e.date}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{
                          background: e.type === 'commission' ? '#007AFF10' : '#34C75910',
                          color: e.type === 'commission' ? '#007AFF' : '#34C759'
                        }}>{e.type === 'commission' ? 'Earned' : 'Payout'}</span>
                      </td>
                      <td className="px-3 py-2" style={{ color: '#0A2540' }}>{e.description}</td>
                      <td className="px-3 py-2 font-medium" style={{ color: '#007AFF' }}>{e.invoice_number || '-'}</td>
                      <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums', color: '#8A94A6' }}>
                        {e.sale_amount > 0 ? `₹${e.sale_amount.toLocaleString()}` : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-medium" style={{ fontVariantNumeric: 'tabular-nums', color: '#007AFF' }}>
                        {e.commission_earned > 0 ? `+₹${e.commission_earned.toLocaleString()}` : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-medium" style={{ fontVariantNumeric: 'tabular-nums', color: '#34C759' }}>
                        {e.payout > 0 ? `-₹${e.payout.toLocaleString()}` : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-bold" style={{ fontVariantNumeric: 'tabular-nums', color: e.running_balance > 0 ? '#FF3B30' : '#34C759' }}>
                        ₹{Math.abs(e.running_balance).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Payout Dialog */}
      {showPayDialog && selectedDoctor && (
        <PayoutDialog doctor={selectedDoctor} pending={ledger?.pending_commission || 0} onClose={() => setShowPayDialog(false)} onPaid={() => { setShowPayDialog(false); loadList(); loadLedger(selectedDoctor.doctor_id); }} />
      )}
    </div>
  );
}

function PayoutDialog({ doctor, pending, onClose, onPaid }) {
  const [amount, setAmount] = useState(pending);
  const [remarks, setRemarks] = useState('Commission payout');
  const [paying, setPaying] = useState(false);

  const pay = async () => {
    setPaying(true);
    try { await api.payDoctorCommission(doctor.doctor_id, { amount, remarks }); onPaid(); } catch {}
    setPaying(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Record Payout - {doctor.doctor_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded" style={{ background: '#F5F6F8' }}>
            <p className="text-xs">Pending commission: <strong style={{ color: '#FF3B30' }}>₹{pending.toLocaleString()}</strong></p>
          </div>
          <div><Label className="text-[10px] font-bold uppercase" style={{ color: '#8A94A6' }}>Amount</Label>
            <Input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} className="h-9 mt-1" /></div>
          <div><Label className="text-[10px] font-bold uppercase" style={{ color: '#8A94A6' }}>Remarks</Label>
            <Input value={remarks} onChange={e => setRemarks(e.target.value)} className="h-9 mt-1" /></div>
          <Button onClick={pay} disabled={paying || amount <= 0} className="w-full h-9 text-sm font-bold" style={{ background: '#34C759', color: 'white' }}>
            {paying ? 'Processing...' : `Pay ₹${amount.toLocaleString()}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
// PASSBOOK TAB (Integrated)
// ═══════════════════════════════════════════════════════
function PassbookTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => { api.getPassbook().then(r => { setEntries(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const lastBalance = entries.length > 0 ? entries[entries.length - 1].running_balance : 0;
  const totalDeposits = entries.filter(e => e.entry_type === 'deposit').reduce((s, e) => s + e.amount, 0);
  const totalWithdrawals = entries.filter(e => e.entry_type === 'withdrawal').reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-3 gap-4">
        <DashCard icon={Wallet} iconColor="#0A2540" iconBg="#0A254010" label="Current Balance" value={`₹${lastBalance.toLocaleString()}`} valueColor="#0A2540" />
        <DashCard icon={ArrowDown} iconColor="#34C759" iconBg="#34C75910" label="Total Deposits" value={`₹${totalDeposits.toLocaleString()}`} valueColor="#34C759" />
        <DashCard icon={ArrowUp} iconColor="#FF3B30" iconBg="#FF3B3010" label="Total Withdrawals" value={`₹${totalWithdrawals.toLocaleString()}`} valueColor="#FF3B30" />
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="h-8 px-4 text-xs font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={14} className="mr-1" /> New Entry
        </Button>
      </div>

      <div className="rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="divide-y" style={{ borderColor: '#E2E4E9' }}>
          {loading ? <div className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</div> :
            [...entries].reverse().map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: e.entry_type === 'deposit' ? '#34C75910' : '#FF3B3010' }}>
                  {e.entry_type === 'deposit' ? <ArrowDown size={16} weight="bold" style={{ color: '#34C759' }} /> : <ArrowUp size={16} weight="bold" style={{ color: '#FF3B30' }} />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: '#0A2540' }}>{e.description}</p>
                  <p className="text-[10px]" style={{ color: '#8A94A6' }}>{e.reference} | {e.created_at?.slice(0, 10)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ color: e.entry_type === 'deposit' ? '#34C759' : '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>
                    {e.entry_type === 'deposit' ? '+' : '-'}₹{e.amount.toLocaleString()}
                  </p>
                  <p className="text-[10px]" style={{ color: '#8A94A6', fontVariantNumeric: 'tabular-nums' }}>Bal: ₹{e.running_balance.toLocaleString()}</p>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New Passbook Entry</DialogTitle></DialogHeader>
          <PassbookForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PassbookForm({ onSave }) {
  const [form, setForm] = useState({ entry_type: 'deposit', amount: 0, description: '', reference: '' });
  const save = async () => { try { await api.createPassbookEntry(form); onSave(); } catch {} };
  return (
    <div className="space-y-3">
      <div><Label className="text-[10px] font-bold uppercase" style={{ color: '#8A94A6' }}>Type</Label>
        <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
          <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="deposit">Deposit</SelectItem><SelectItem value="withdrawal">Withdrawal</SelectItem></SelectContent>
        </Select></div>
      <div><Label className="text-[10px] font-bold uppercase" style={{ color: '#8A94A6' }}>Amount</Label>
        <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} className="h-9 mt-1" /></div>
      <div><Label className="text-[10px] font-bold uppercase" style={{ color: '#8A94A6' }}>Description</Label>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-9 mt-1" /></div>
      <div><Label className="text-[10px] font-bold uppercase" style={{ color: '#8A94A6' }}>Reference</Label>
        <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} className="h-9 mt-1" /></div>
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>Save</Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// AUDIT TRAIL TAB
// ═══════════════════════════════════════════════════════
function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.getKhataAudit().then(r => { setLogs(r.data); setLoading(false); }); }, []);

  return (
    <div className="mt-4">
      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: '#E2E4E9' }}>
          <ClockCounterClockwise size={16} style={{ color: '#007AFF' }} />
          <h3 className="text-sm font-semibold" style={{ color: '#0A2540' }}>Transaction Audit Trail</h3>
        </div>
        <table className="w-full text-xs" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead><tr style={{ background: '#F9F9FA' }}>
            {['Timestamp', 'Action', 'User', 'Details'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr> :
              logs.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No audit logs yet</td></tr> :
              logs.map((l, i) => (
                <tr key={i} className="hover:bg-slate-50" style={{ borderBottom: '1px solid #F5F6F8' }}>
                  <td className="px-4 py-2" style={{ color: '#8A94A6' }}>{l.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-2">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{
                      background: l.action === 'created' ? '#34C75910' : l.action === 'deleted' ? '#FF3B3010' : '#007AFF10',
                      color: l.action === 'created' ? '#34C759' : l.action === 'deleted' ? '#FF3B30' : '#007AFF'
                    }}>{l.action}</span>
                  </td>
                  <td className="px-4 py-2 font-medium" style={{ color: '#0A2540' }}>{l.user_name}</td>
                  <td className="px-4 py-2" style={{ color: '#8A94A6' }}>{l.details}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}
